import noop from "lodash/noop";
import { useTranslation } from "react-i18next";
import React from "react";
import ReactTooltip from "react-tooltip";
import { useTheme } from "styled-components";
import styled from "styled-components/macro";
import { DeletableChips } from "../DeletableChips/DeletableChips";
import { InfoIcon } from "../Icons/Icons";
import type { AttributeSchema } from "../../types/types.PIM";
import { removeUnderscore, toTitleCase } from "../../util/util";
import { H6 } from "../Typography/Typography";
import { TextSmRegular } from "../Typography/TypographyV2";
import { getPreviewAttributeLabel } from "./utils";
import { useMdmEntityDisplayNames } from "../../util/mdmEntityDisplayNames";

const AttributeItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
`;

const LabelWithHint = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_sm")};
`;

function PreviewAttributesLabelWithInfo({
  label,
  tooltipId,
  tooltipText,
}: {
  label: string;
  tooltipId: string;
  tooltipText: string;
}) {
  const theme = useTheme();
  return (
    <>
      <LabelWithHint>
        <TextSmRegular color="secondary">{label}</TextSmRegular>
        <span
          data-tip={tooltipText}
          data-for={tooltipId}
          style={{ display: "inline-flex", cursor: "help" }}
          aria-label={tooltipText}
        >
          <InfoIcon fill={theme.secondaryIconColor} width={14} height={14} />
        </span>
      </LabelWithHint>
      <ReactTooltip id={tooltipId} delayHide={200} effect="solid" multiline />
    </>
  );
}

type ObjectReferenceDetailProps = {
  attribute: Extract<AttributeSchema, { input_type: "object_reference" }>;
};

export const ObjectReferenceDetail = ({
  attribute,
}: ObjectReferenceDetailProps) => {
  const { t } = useTranslation();
  const { getObjectTypeLabel } = useMdmEntityDisplayNames();
  const sourcePreviewTooltipId = `object-ref-detail-source-preview-${attribute.id}`;
  const targetPreviewTooltipId = `object-ref-detail-target-preview-${attribute.id}`;

  return (
    <>
      <AttributeItem>
        <TextSmRegular color="secondary">
          {t("Source Object Type")}
        </TextSmRegular>
        <H6>{getObjectTypeLabel(attribute.source_object_type)}</H6>
      </AttributeItem>
      <AttributeItem>
        <TextSmRegular color="secondary">
          {t("Target Object Type")}
        </TextSmRegular>
        <H6>{getObjectTypeLabel(attribute.target_object_type)}</H6>
      </AttributeItem>
      <AttributeItem>
        <TextSmRegular color="secondary">
          {t("Target-Side Display Name")}
        </TextSmRegular>
        <H6>{attribute.target_side_display_name || "--"}</H6>
      </AttributeItem>
      <AttributeItem>
        <TextSmRegular color="secondary">{t("Selection Type")}</TextSmRegular>
        <H6>
          {toTitleCase(
            removeUnderscore(
              attribute.is_multiple_choice
                ? "Multi Reference"
                : "Single Reference"
            )
          )}
        </H6>
      </AttributeItem>
      <AttributeItem>
        <PreviewAttributesLabelWithInfo
          label={t("Source Object Preview Attributes")}
          tooltipId={sourcePreviewTooltipId}
          tooltipText={t(
            "These attributes will be displayed when previewing the source object"
          )}
        />
        <DeletableChips
          chips={
            attribute.source_preview_attributes?.map((attr) => ({
              name: getPreviewAttributeLabel(attr),
              active: false,
            })) ?? []
          }
          noneSelectedName={t("Source preview attributes")}
          handleClick={noop}
        />
      </AttributeItem>
      <AttributeItem>
        <PreviewAttributesLabelWithInfo
          label={t("Target Object Preview Attributes")}
          tooltipId={targetPreviewTooltipId}
          tooltipText={t(
            "These attributes will be displayed when previewing the target object"
          )}
        />
        <DeletableChips
          chips={
            attribute.target_preview_attributes?.map((attr) => ({
              name: getPreviewAttributeLabel(attr),
              active: false,
            })) ?? []
          }
          noneSelectedName={t("Target preview attributes")}
          handleClick={noop}
        />
      </AttributeItem>
    </>
  );
};
